import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, ModalController } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { TranslateService } from '@ngx-translate/core';
import { Subject, lastValueFrom } from 'rxjs';
import { AlertModalComponent } from 'src/app/components/alert-modal/alert-modal.component';
import { requestResetPasswordModel } from 'src/app/models/User.model';
import { AuthService } from 'src/app/services/auth.service';
import { LanguageService } from 'src/app/services/language/language.service';
import { SharedService } from 'src/app/services/shared/shared.service';
import { UserService } from 'src/app/services/user/user.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
  standalone: false,
})
export class LoginPage {
  constructor(
    private route: Router,
    private routeParams: ActivatedRoute,
    private loginService: UserService,
    private userService: UserService,
    private authService: AuthService,
    private sharedService: SharedService,
    private storage: Storage,
    private translateService: TranslateService,
    public lang: LanguageService,
    private modalController: ModalController,
    private alertController: AlertController,
  ) {}

  public appVersion = (environment.id != 'PRD' ? environment.id + ' - ' : '') + environment.version;
  notifier$ = new Subject();

  email!: string;
  password!: string;

  userId!: string;
  userLogin!: string;
  recoveryPassword = false;

  new_password!: string;
  validate_password!: string;

  btnEnabled = true;
  showPassword = false;
  showRepeatPassword = false;
  changePass = true;
  uppercase = false;
  passLength = false;
  characterSpecial = false;
  characterNumbers = false;
  diffError = false;
  adLogin = true;
  loginDisplay = false;
  dataSource: any = [];
  apiId!: string;
  firstLogin = false;
  firstLoginEmail!: string;
  emailConfirmed = false;

  domains = [
    'csnpompeia.com.br',
    'fsnt.com.br',
    'grupojacto.com.br',
    'jacto.com.ar',
    'jacto.com.br',
    'jacto.onmicrosoft.com',
    'mizumo.com.br',
    'otmis.com.br',
    'otmisnet.com.br',
    'rodojacto.com.br',
    'sintegrasciencies.com.br',
    'sintegrasurgical.com.br',
    'tridon.com.br',
    'unipac.com.br',
  ];
  validEmail = false;

  section: 'login' | 'recovery' | 'newSession' | 'reset' | 'confirmEmail' = 'login';

  async ionViewWillEnter() {
    try {
      if (this.authService.isAuthenticated()) {
        await this.route.navigateByUrl('/sso/callback');
        return;
      }
    } catch (e) {
      console.warn('Erro ao verificar autenticação inicial:', e);
    }

    await this.sharedService.showLoading();

    await this.lang.setLanguage(this.translateService.getBrowserLang() ?? '');
    let token = '';
    await this.storage.set('pass', 0);

    await this.storage.get('token').then((res: string) => {
      token = res;
    });

    if (this.route.url.includes('confirmEmail')) {
      await this.confirmEmail();
    } else {
      this.userId = this.routeParams.snapshot.paramMap.get('passwordResetRequestId') ?? '';
      this.userId ? (this.section = 'reset') : (this.section = 'login');

      if (!token && this.section != 'reset') {
        this.userLogin = '';
        this.email = '';
        this.password = '';
        this.showPassword = false;
        this.adLogin = true;
        this.recoveryPassword = false;
      } else if (this.section != 'reset') {
        this.route.navigateByUrl('/panel');
      }
    }
    this.sharedService.dismissLoading();
  }

  async confirmEmail() {
    const token = this.routeParams.snapshot.paramMap.get('token') ?? '';
    if (token) {
      this.section = 'confirmEmail';
      await lastValueFrom(this.userService.confirmeEmail(token)).then(
        async (res: any) => {
          await this.sharedService.showToast(res[this.translateService.currentLang], 10000);

          this.emailConfirmed = true;
        },
        _ => {
          this.section = 'login';
        },
      );
    }
  }

  async closeModals() {
    await this.modalController.dismiss();
  }

  async validateLogin() {
    this.btnEnabled = false;

    try {
      const result = await lastValueFrom(this.loginService.validateLogin(this.userLogin, this.password));
      if (result.passwordResetRequestId) {
        this.requestNewPassword(result.firstLogin, result.passwordResetRequestId);
        return;
      }
      if (result.retired) {
        await this.loginWithNP();
        return;
      }
      this.redirectToSSOTransition(result.ssoEmail, result.computerized, result.migratedAd);
      this.btnEnabled = true;
    } catch (err) {
      this.handleLoginError(err);
      this.btnEnabled = true;
    }
  }

  async loginWithNP() {
    this.btnEnabled = false;
    try {
      const result = await lastValueFrom(this.loginService.signin(this.userLogin, this.password, true));
      if (result.passwordResetRequestId) {
        this.requestNewPassword(result.firstLogin, result.passwordResetRequestId);
        return;
      }

      // Fallback: se não tiver email corporativo, segue fluxo normal
      let route = null;
      route = localStorage.getItem('previousRoute');
      const isRetiredEmployee = result.retired;
      const isRetireeRoute = route && route.startsWith('/retiree');

      if (route) {
        if (isRetiredEmployee && !isRetireeRoute) {
          await this.route.navigateByUrl('/retiree');
        } else if (!isRetiredEmployee && isRetireeRoute) {
          await this.route.navigateByUrl('/panel');
        } else {
          await this.route.navigateByUrl(route);
        }
        localStorage.removeItem('previousRoute');
      } else {
        await this.route.navigateByUrl(isRetiredEmployee ? '/retiree' : '/panel');
      }
      this.btnEnabled = true;

      let emailConfirmed = await this.storage.get('emailConfirmed');
      if (!emailConfirmed) {
        this.openModalConfirmEmail();
      }
    } catch (err) {
      this.handleLoginError(err);
      this.btnEnabled = true;
    }
  }

  async handleLoginError(err: unknown) {
    if (err instanceof HttpErrorResponse) {
      const loginBlockedAt = err.error?.loginblockedat;
      if (loginBlockedAt) {
        await this.alertLoginBlocked(loginBlockedAt);
        return;
      }

      const apiError = this.parseApiMessage(err);
      if (apiError && apiError.pt.includes('Tentativas restantes:')) {
        await this.alertResetPassword(apiError.pt);
      }
    }
  }

  private parseApiMessage(err: HttpErrorResponse) {
    if (err.error?.message && typeof err.error.message === 'string') {
      try {
        return JSON.parse(err.error.message);
      } catch (error) {
        return null;
      }
    }

    return null;
  }

  requestNewPassword(firstLogin: boolean, passwordResetRequestId: string) {
    this.sharedService.showToast(
      this.translateService.instant(firstLogin ? 'common.resetPasswordFirst' : 'common.resetPassword'),
      4000,
    );
    this.firstLogin = firstLogin;
    this.section = 'reset';
    this.new_password = '';
    this.apiId = passwordResetRequestId;
    this.btnEnabled = true;
  }

  redirectToSSOTransition(email: string | null, computerized: boolean, migrated: boolean) {
    this.route.navigateByUrl('/sso/transition', { state: { email, computerized, migrated, handle: true } });
  }

  async openModalConfirmEmail() {
    const title = 'Confirmação de e-mail';
    const typeAlert = 'confirmEmail';
    const modal = await this.modalController.create({
      component: AlertModalComponent,
      componentProps: {
        title,
        typeAlert,
      },
      backdropDismiss: true,
      cssClass: 'j-medium-modal',
    });
    await modal.present();
  }

  async alertResetPassword(message: string) {
    let filterMessage = message.split(':');
    if (Number(filterMessage[1].trim()) == 1) {
      const alert = await this.alertController.create({
        header: this.translateService.instant('login.label.alert.loginBlockedCareful.title'),
        message: this.translateService.instant('login.label.alert.loginBlockedCareful.message'),
        buttons: [
          {
            text: this.translateService.instant('common.cancel'),
            role: 'cancel',
          },
          {
            text: this.translateService.instant('login.label.alert.loginBlockedCareful.button.confirm'),
            role: 'confirm',
            handler: () => {
              this.section = 'recovery';
              this.modalController.dismiss();
            },
          },
        ],
        cssClass: 'custon-alert',
        mode: 'ios',
      });
      await alert.present();
    }
  }

  async alertLoginBlocked(time: string) {
    let date = new Date(time).getMinutes();
    let timeNow = new Date().getMinutes();
    let timeToWait = 15 - (timeNow - date);
    const alert = await this.alertController.create({
      header: this.translateService.instant('login.label.alert.loginBlocked'),
      message: this.translateService.instant('login.label.alert.awaitLogin', {
        time: timeToWait,
      }),
      buttons: ['OK'],
      cssClass: 'custon-alert',
      mode: 'ios',
    });
    await alert.present();
  }

  async verifyEmail() {
    this.btnEnabled = false;
    let email = this.userLogin.toLowerCase();

    if (new RegExp(this.domains.join('|')).test(email)) {
      await this.sharedService.showToast(this.translateService.instant('login.alert.notEmail'));
      this.btnEnabled = true;
    } else {
      this.adLogin = false;
      this.btnEnabled = true;
    }
  }

  async recovery() {
    this.btnEnabled = false;

    let obj: requestResetPasswordModel = {
      backoffice: false,
      languageCode: this.translateService.currentLang,
      emailResetPassword: this.email,
    };

    await lastValueFrom(this.userService.recovery(obj)).then(
      res => {
        this.recoveryPassword = true;
        this.sharedService.showToast(this.translateService.instant('toast.success.SendEmail'));
        this.btnEnabled = true;
      },
      () => {
        this.btnEnabled = true;
      },
    );
    this.email = '';
  }

  async resetPassword() {
    !this.apiId ? (this.apiId = this.routeParams.snapshot.paramMap.get('passwordResetRequestId') ?? '') : null;
    this.btnEnabled = false;
    let obj = {
      username: this.userLogin,
      password: this.new_password,
      requestPasswordId: this.apiId,
      emailResetPassword: this.firstLogin ? this.firstLoginEmail : '',
    };

    await this.loginService
      .resetPassword(obj)
      .toPromise()
      .then(
        async () => {
          this.translateService.get('password.changed.success').subscribe(res => {
            this.sharedService.showToast(res);
          });
          this.password = obj.password;
          await this.validateLogin();
        },
        () => {
          this.btnEnabled = true;
        },
      );
  }

  checkEmail() {
    // /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    let valid = String(this.firstLoginEmail)
      .toLowerCase()
      .match(/^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/);

    if (valid !== null) {
      this.validEmail = true;
    } else {
      this.validEmail = false;
    }
  }

  checkPassword() {
    function validateUppercase(pass: string) {
      return /[A-Z]/.test(pass);
    }

    function ValidateCharacterSpecial(pass: string) {
      return /[!@#$%*()_+^&{}}:;?.]/.test(pass);
    }

    function ValidateCharacterNumbers(pass: string) {
      if (/[0-9]/.test(pass)) {
        return true;
      } else {
        return false;
      }
    }

    validateUppercase(this.new_password) ? (this.uppercase = true) : (this.uppercase = false);
    this.new_password.length >= 9 ? (this.passLength = true) : (this.passLength = false);
    ValidateCharacterSpecial(this.new_password) ? (this.characterSpecial = true) : (this.characterSpecial = false);
    ValidateCharacterNumbers(this.new_password) ? (this.characterNumbers = true) : (this.characterNumbers = false);
  }

  ngOnDestroy() {
    this.notifier$.next(null);
    this.notifier$.complete();
  }

  loginSSO() {
    this.authService.loginRedirect();
  }
}
